// src/pages/admin/BranchList.jsx
import React, { useCallback, useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftSnackbar from "components/SoftSnackbar";
import SoftButton from "components/SoftButton";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import DataTable from "components/QuestDataTable";
import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import FlagLoader from "layouts/flagloader";

const parseStoredPermissions = () => {
  try {
    const stored = localStorage.getItem("permissions");
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const BranchList = () => {
  const { isAuthReady, permissions = [], userPermission = [] } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [deletingId, setDeletingId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const navigate = useNavigate();

  const grantedPermissions = useMemo(
    () => Array.from(new Set([...permissions, ...userPermission, ...parseStoredPermissions()])),
    [permissions, userPermission]
  );

  const hasBranchPermission = useCallback(
    (...requiredPermissions) => {
      if (grantedPermissions.includes("access.system.full_access")) return true;
      return requiredPermissions
        .flat()
        .filter(Boolean)
        .some((permission) => grantedPermissions.includes(permission));
    },
    [grantedPermissions]
  );

  const canViewPage = hasBranchPermission("branch.pages.list.view");
  const canCreate =
    hasBranchPermission("branch.pages.create.view") &&
    hasBranchPermission("branch.actions.open_create");
  const canOpenTrashed =
    hasBranchPermission("branch.pages.trashed.view") &&
    hasBranchPermission("branch.actions.open_trashed");
  const canOpenEdit =
    hasBranchPermission("branch.pages.edit.view") &&
    hasBranchPermission("branch.actions.open_edit");
  const canDelete = hasBranchPermission("branch.actions.delete");
  const canConfirmDelete = hasBranchPermission("branch.actions.confirm_delete");
  const canCancelDelete = hasBranchPermission("branch.actions.cancel_delete");
  const canViewField = useCallback(
    (field) => hasBranchPermission(`branch.fields.${field}.view`),
    [hasBranchPermission]
  );

  const fetchBranches = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/branches`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Failed to fetch branches");
      }
      const { data } = await res.json();
      setBranches(data || []);
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Error loading branches", color: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthReady) return;
    if (!canViewPage) {
      setLoading(false);
      return;
    }
    fetchBranches();
  }, [canViewPage, fetchBranches, isAuthReady]);

  const handleDeleteClick = (branch) => {
    if (!canDelete) {
      setSnackbar({ open: true, message: "You do not have permission to delete branches", color: "error" });
      return;
    }
    setSelectedBranch(branch);
    setDeleteDialogOpen(true);
  };

  const handleEditClick = (branch) => {
    if (!canOpenEdit) {
      setSnackbar({ open: true, message: "You do not have permission to edit branches", color: "error" });
      return;
    }
    navigate(`/branches/edit/${branch.id}`);
  };

  const handleDeleteConfirm = async () => {
    if (!canDelete || !canConfirmDelete) {
      setSnackbar({ open: true, message: "You do not have permission to confirm delete", color: "error" });
      return;
    }

    try {
      setDeletingId(selectedBranch.id);
      const token = localStorage.getItem("authToken");
      const res = await fetch(`${API_URL}/branches/${selectedBranch.id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || "Delete failed");
      }
      setSnackbar({ open: true, message: "Branch deleted successfully", color: "success" });
      fetchBranches();
    } catch (err) {
      setSnackbar({ open: true, message: err.message || "Delete failed", color: "error" });
    } finally {
      setDeletingId(null);
      setDeleteDialogOpen(false);
      setSelectedBranch(null);
    }
  };

  const closeDeleteDialog = () => {
    if (!canCancelDelete) return;
    setDeleteDialogOpen(false);
    setSelectedBranch(null);
  };

  const columns = [];
  if (canViewField("name")) columns.push({ Header: "Name", accessor: "name" });
  if (canViewField("code")) columns.push({ Header: "Code", accessor: "code" });
  if (canViewField("address")) columns.push({ Header: "Address", accessor: "address" });
  if (canViewField("status")) columns.push({ Header: "Status", accessor: "status" });
  if (canViewField("actions") && (canOpenEdit || canDelete)) {
    columns.push({ Header: "Actions", accessor: "actions", disableSortBy: true });
  }

  const rows = branches.map((branch) => ({
    name: branch.name,
    code: branch.code,
    address: branch.address,
    status: (
      <SoftBox display="inline-flex" alignItems="center" px={1.5} py={0.5} borderRadius={1}>
        <SoftTypography variant="caption" color={branch.status ? "info" : "error"}>
          {branch.status ? "ACTIVE" : "INACTIVE"}
        </SoftTypography>
      </SoftBox>
    ),
    actions: (
      <>
        {canOpenEdit && (
          <Tooltip title="Edit Branch">
            <IconButton size="small" onClick={() => handleEditClick(branch)} aria-label="edit">
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {canDelete && (
          <Tooltip title="Delete Branch">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDeleteClick(branch)}
              aria-label="delete"
              disabled={deletingId === branch.id}
            >
              {deletingId === branch.id ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <DeleteIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </>
    ),
  }));

  if (!isAuthReady || (canViewPage && loading)) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox display="flex" justifyContent="center" alignItems="center" height="70vh">
          <FlagLoader />
        </SoftBox>
      </DashboardLayout>
    );
  }

  if (!canViewPage) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Branch List
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to view branches.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Branch Management"
        content={snackbar.message}
        open={snackbar.open}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        time={3000}
      />

      <SoftBox p={3}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <SoftTypography variant="h4" fontWeight="bold">
            Branch List
          </SoftTypography>
          <SoftBox display="flex" gap={1} flexWrap="wrap" justifyContent="flex-end">
            {canCreate && (
              <SoftButton
                size="small"
                color="info"
                startIcon={<Icon>add</Icon>}
                onClick={() => navigate("/branches/add")}
              >
                Add New Branch
              </SoftButton>
            )}
            {canOpenTrashed && (
              <SoftButton
                size="small"
                color="dark"
                variant="outlined"
                startIcon={<Icon>delete</Icon>}
                onClick={() => navigate("/branches/trashed")}
              >
                Trashed Branch List
              </SoftButton>
            )}
          </SoftBox>
        </SoftBox>

        <Card>
          <DataTable table={{ columns, rows }} />
        </Card>
      </SoftBox>

      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <SoftTypography variant="body2">
            Are you sure you want to delete the branch <strong>{selectedBranch?.name}</strong>?
          </SoftTypography>
        </DialogContent>
        <DialogActions>
          {canConfirmDelete && (
            <SoftButton
              color="info"
              variant="contained"
              onClick={handleDeleteConfirm}
              disabled={deletingId === selectedBranch?.id}
              endIcon={deletingId === selectedBranch?.id ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {deletingId === selectedBranch?.id ? "Processing..." : "Confirm"}
            </SoftButton>
          )}
          {canCancelDelete && (
            <SoftButton variant="contained" color="error" onClick={closeDeleteDialog}>
              Cancel
            </SoftButton>
          )}
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

export default BranchList;
